<div
  [attr.dir]="currentLang === 'ar' ? 'rtl' : 'ltr'"
  class="min-h-screen bg-gray-50"
>
  <div class="container mx-auto px-6 py-8">
    <!-- Section Header -->
    <div class="mb-8">
      <h2 class="text-3xl font-bold text-gray-900 mb-2">
        {{ "DASHBOARD.POLICIES.TITLE" | translate }}
      </h2>
      <p class="text-gray-600">
        {{ "DASHBOARD.POLICIES.DESCRIPTION" | translate }}
      </p>
    </div>

    <!-- Policy Controls -->
    <div
      class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8 bg-white p-6 rounded-xl shadow-sm"
    >
      <!-- Search Box -->
      <div class="relative flex-1 max-w-md">
        <input
          type="text"
          [(ngModel)]="searchParams.searchTerm"
          (keyup)="onSearchKeyup()"
          [placeholder]="'DASHBOARD.POLICIES.SEARCH_PLACEHOLDER' | translate"
          class="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
        <svg
          class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
        <button
          *ngIf="hasSearchCriteria()"
          (click)="clearSearch()"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          [title]="'DASHBOARD.POLICIES.CLEAR_SEARCH' | translate"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button
          (click)="purchaseNewPolicy()"
          class="bg-teal-500 hover:bg-teal-600 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            ></path>
          </svg>
          {{ "DASHBOARD.POLICIES.PURCHASE_NEW_POLICY" | translate }}
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex flex-wrap gap-2 mb-6">
      <button
        *ngFor="let tab of tabs"
        (click)="selectTab(tab.name)"
        [class]="
          selectedTab === tab.name
            ? 'bg-teal-500 text-white border-teal-500'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        "
        class="px-4 py-3 rounded-lg border flex items-center gap-2 transition-colors font-medium"
      >
        <svg
          *ngIf="tab.name === 'active'"
          class="w-4 h-4"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          ></path>
        </svg>
        <svg
          *ngIf="tab.name === 'renew'"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          ></path>
        </svg>

        {{ tab.label | translate }} ({{ tab.count }})
      </button>
    </div>

    <!-- Loading State -->
    <app-loader
      *ngIf="loading"
      [show]="loading"
      [mainMessage]="'DASHBOARD.POLICIES.LOADING_MESSAGE' | translate"
      [subMessage]="'DASHBOARD.POLICIES.LOADING_SUB_MESSAGE' | translate"
    >
    </app-loader>

    <!-- Error Message -->
    <div
      *ngIf="error && !isSearching && !loading"
      class="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4 rounded-lg mb-6 flex items-center gap-3"
    >
      <svg
        class="w-5 h-5 text-yellow-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
        ></path>
      </svg>
      {{ error }}
    </div>

    <!-- Policies List -->
    <div *ngIf="!loading && !error" class="space-y-6">
      <!-- Policy Cards -->
      <div
        *ngFor="let policy of getPoliciesForTab(selectedTab)"
        class="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
      >
        <!-- Policy Status Header -->
        <div
          class="bg-gray-50 px-6 py-4 border-b flex items-center justify-between"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
              [ngClass]="
                getProviderColor(policy.quoteResponse?.providerName || '')
              "
            >
              {{
                getProviderInitials(policy.quoteResponse?.providerName || "")
              }}
            </div>
            <span class="font-semibold text-gray-900">{{
              policy.quoteResponse?.providerName
            }}</span>
            <span
              [class]="getStatusClass(policy.status)"
              class="px-3 py-1 rounded-full text-xs font-medium"
            >
              {{ policy.status }}
            </span>
            <span class="text-gray-600 text-sm">{{
              policy.type || ("DASHBOARD.POLICIES.HEALTH_INSURANCE" | translate)
            }}</span>
          </div>
        </div>

        <!-- Policy Information -->
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <!-- Company Info -->
            <div>
              <label class="block text-sm font-medium text-gray-500 mb-1">{{
                "DASHBOARD.POLICIES.COMPANY" | translate
              }}</label>
              <p class="font-semibold text-gray-900">
                {{ policy.providerName }}
              </p>
              <p class="text-sm text-gray-600">
                {{ policy.quoteResponse?.quoteRequest?.companyId || "N/A" }} -
                {{ "DASHBOARD.POLICIES.INSURANCE_PROVIDER" | translate }}
              </p>
            </div>

            <!-- Policy Period -->
            <div>
              <label class="block text-sm font-medium text-gray-500 mb-1">{{
                "DASHBOARD.POLICIES.POLICY_PERIOD" | translate
              }}</label>
              <p class="text-gray-900">
                {{ policy.startDate | date : "MMM dd, yyyy" }} -
                {{
                  policy.endDate
                    | date
                      : "MMM dd,
                yyyy"
                }}
              </p>
            </div>

            <!-- Location -->
            <div>
              <label class="block text-sm font-medium text-gray-500 mb-1">{{
                "DASHBOARD.POLICIES.LOCATION" | translate
              }}</label>
              <p class="text-gray-900">
                {{ "DASHBOARD.POLICIES.SAUDI_ARABIA" | translate }}
              </p>
            </div>

            <!-- Premium -->
            <div class="text-right">
              <span class="text-2xl font-bold text-gray-900">{{
                policy.premiumAmount || policy.totalPremium || 0
                  | currency : policy.currency || "SAR" : "symbol" : "1.0-0"
              }}</span>
              <span class="block text-sm text-gray-500">{{
                "DASHBOARD.POLICIES.ANNUAL_PREMIUM" | translate
              }}</span>
            </div>
          </div>
        </div>

        <!-- Policy Actions -->
        <div class="bg-gray-50 px-6 py-4 flex flex-wrap gap-3">
          <button
            (click)="viewDetails(policy)"
            class="bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              ></path>
            </svg>
            {{ "DASHBOARD.POLICIES.VIEW_DETAILS" | translate }}
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div
        *ngIf="getPoliciesForTab(selectedTab).length === 0"
        class="text-center py-12 bg-white rounded-xl shadow-sm"
      >
        <svg
          class="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          ></path>
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          {{ getEmptyMessage() }}
        </h3>
        <p class="text-gray-500">
          {{ "DASHBOARD.POLICIES.NO_POLICIES_FOUND" | translate }}
        </p>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 gap-4 mt-8">
      <!-- Active Policies -->
      <div class="bg-white rounded-xl shadow-md p-6 relative overflow-hidden">
        <div class="flex items-start justify-between mb-4">
          <div
            class="w-12 h-12 bg-[#F0FDFA] rounded-lg flex items-center justify-center"
          >
            <svg
              class="w-6 h-6 text-teal-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
        </div>
        <h3 class="text-gray-600 text-sm font-medium mb-2">
          {{ "DASHBOARD.POLICIES.ACTIVE_POLICIES" | translate }}
        </h3>
        <p class="text-3xl font-bold text-gray-900">
          {{ activePolicies.length }}
        </p>
      </div>

      <!-- Renew Policies -->
      <div class="bg-white rounded-xl shadow-md p-6 relative overflow-hidden">
        <div class="flex items-start justify-between mb-4">
          <div
            class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"
          >
            <svg
              class="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              ></path>
            </svg>
          </div>
        </div>
        <h3 class="text-gray-600 text-sm font-medium mb-2">
          {{ "DASHBOARD.POLICIES.RENEW_POLICIES" | translate }}
        </h3>
        <p class="text-3xl font-bold text-gray-900">
          {{ renewPolicies.length }}
        </p>
      </div>
    </div>
  </div>
</div>
